import { useEffect, useRef } from 'react'
import { findNode, NodeKind, type ImageNode } from '@/core/document'
import { useEditSession } from '@/core/store/editSession'
import { TEST_IDS } from '@/lib/testIds'
import { cn } from '@/lib/utils'
import { TOOLS } from '@/tools/registry'
import type { ToolTab } from '@/tools/types'

export function ToolStrip() {
  const document = useEditSession((state) => state.document)
  const selectedNodeId = useEditSession((state) => state.selectedNodeId)
  const activeTab = useEditSession((state) => state.activeTab)
  const setActiveTab = useEditSession((state) => state.setActiveTab)
  const activeButtonRef = useRef<HTMLButtonElement>(null)
  if (!document) throw new Error('Tool strip requires a loaded document')
  const selected = selectedNodeId ? findNode(document.root, selectedNodeId) : null
  if (selectedNodeId && !selected) throw new Error(`Active photo "${selectedNodeId}" is missing from the document`)
  if (selected && selected.kind !== NodeKind.Image) {
    throw new Error(`Active photo must be an Image node, received "${selected.kind}"`)
  }
  const activePhoto: ImageNode | null = selected
  const visibleTools = TOOLS.filter((tool) => tool.appliesTo(activePhoto))

  useEffect(() => {
    activeButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
  }, [activeTab])

  return (
    <div className="shrink-0 border-b p-2">
      <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Editing tools" data-testid={TEST_IDS.toolStrip}>
        {visibleTools.map((tool) => {
          const Icon = tool.icon
          const active = tool.tab === activeTab
          return (
            <button
              key={tool.tab}
              ref={active ? activeButtonRef : undefined}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={TEST_IDS.toolTab(tool.tab as ToolTab)}
              onClick={() => setActiveTab(tool.tab)}
              className={cn(
                'flex min-w-[4.25rem] shrink-0 flex-col items-center gap-1 rounded-md px-2 py-2 text-xs font-medium text-muted-foreground outline-offset-2 transition hover:bg-muted hover:text-foreground',
                active && 'bg-muted text-foreground',
              )}
            >
              <Icon className="size-4" aria-hidden="true" />
              <span>{tool.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
